import React from "react";
import { motion } from "framer-motion";

export default function GamesPortfolioSection() {
  const games = [
    {
      title: "Card Games",
      category: "Real-Time Multiplayer",
      desc: "Poker, Rummy, and other card games made for fun and social play—no real-money or betting.",
      features: [
        "Smooth real-time multiplayer with matchmaking",
        "Social play with friends & tables",
        "Built purely for entertainment and skill",
      ],
      tech: ["Unity", "Node.js", "Socket.io", "MongoDB"],
      icon: "🃏",
      color: "#00d4ff",
    },
    {
      title: "Board Games",
      category: "Multiplayer",
      desc: "Ludo, Carrom, and classic board games with real-time play and friendly competition.",
      features: [
        "Real-time multiplayer matches",
        "Leaderboards & social features",
        "Classic games, modern smooth feel",
      ],
      tech: ["Unity", "Node.js", "Socket.io", "Redis"],
      icon: "🎲",
      color: "#667eea",
    },
    {
      title: "Sports Games",
      category: "Interactive",
      desc: "Soccer, cricket, and sports-style games with smooth controls and competitive multiplayer.",
      features: [
        "Responsive controls & realistic physics",
        "Competitive online multiplayer",
        "Fast, fluid gameplay",
      ],
      tech: ["Unity", "Photon", "C#", "PlayFab"],
      icon: "⚽",
      color: "#ff6b35",
    },
    {
      title: "Casual & Puzzle Games",
      category: "Mobile-First",
      desc: "Engaging puzzle and casual games designed to keep players coming back every day.",
      features: [
        "Level progression & daily challenges",
        "Mobile-first, lightweight design",
        "Social features to boost engagement",
      ],
      tech: ["Unity", "C#", "Firebase", "AdMob"],
      icon: "🧩",
      color: "#fa709a",
    },
  ];

  return (
    <section className="games-portfolio" id="games">
      <div className="games-header">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Games We Build</h2>
          <p className="section-subtitle">
            The kinds of games we can create for you—fun, smooth, and made to keep players coming back
          </p>
          <p className="games-disclaimer">
            🎮 We build games purely for entertainment and skill. We do not develop
            real-money, betting, or gambling games.
          </p>
        </motion.div>
      </div>

      <div className="games-grid">
        {games.map((game, i) => (
          <motion.div
            key={i}
            className="game-portfolio-card"
            style={{ "--card-color": game.color }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.6 }}
            whileHover={{ y: -12, boxShadow: `0 28px 60px ${game.color}33` }}
          >
            {/* Glow header */}
            <div
              className="game-glow-header"
              style={{
                background: `linear-gradient(135deg, ${game.color}26, ${game.color}08)`,
                borderBottom: `1px solid ${game.color}33`,
              }}
            >
              <span
                className="game-category"
                style={{
                  color: game.color,
                  background: `${game.color}1f`,
                  borderColor: `${game.color}4d`,
                }}
              >
                {game.category}
              </span>
              <div
                className="game-icon-large"
                style={{
                  background: `linear-gradient(135deg, ${game.color}, ${game.color}99)`,
                  boxShadow: `0 10px 28px ${game.color}66`,
                }}
              >
                {game.icon}
              </div>
            </div>

            {/* Body */}
            <div className="game-body">
              <h3>{game.title}</h3>
              <p className="game-desc">{game.desc}</p>

              <ul className="game-features">
                {game.features.map((f, idx) => (
                  <li key={idx}>
                    <span
                      className="game-check"
                      style={{ color: game.color }}
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="game-tech-stack">
                <div className="tech-label">Built with</div>
                <div className="tech-tags">
                  {game.tech.map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>

              <motion.a
                href="#contact"
                className="game-view-btn"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
              >
                Discuss Your Game
              </motion.a>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="games-cta"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
      >
        <h3>Have a Game Idea?</h3>
        <p>Share your idea and let's bring it to life together.</p>
        <motion.a
          href="#contact"
          className="cta-game-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Let's Talk
        </motion.a>
      </motion.div>
    </section>
  );
}
